import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md transition-all duration-300">
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-database-2-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
                Kakobuy <span className="text-primary-500">Finds</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3 md:gap-4">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-menu-search-line text-base"></i>
                  <span>Browse Categories</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 border border-background-200 rounded-xl overflow-hidden max-h-[420px] overflow-y-auto">
                    <div className="py-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-150 cursor-pointer"
                          onClick={() => setIsDropdownOpen(false)}
                        >
                          <div className="w-7 h-7 flex items-center justify-center">
                            <i className={`${cat.icon} text-base`}></i>
                          </div>
                          <span className="whitespace-nowrap">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-2xl text-foreground-800"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        {/* Dark backdrop */}
        <div
          className="absolute inset-0 bg-foreground-900/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>

        {/* Slide-in panel */}
        <div
          className={`absolute top-0 right-0 h-full w-[85vw] max-w-[380px] bg-background-50 shadow-2xl transition-transform duration-300 ease-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
            <Link
              to="/"
              className="flex items-center gap-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-database-2-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading text-lg font-bold text-foreground-900 whitespace-nowrap">
                Kakobuy <span className="text-primary-500">Finds</span>
              </span>
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Close menu"
            >
              <i className="ri-close-line text-xl text-foreground-700"></i>
            </button>
          </div>

          {/* Panel body - scrollable category list */}
          <div className="overflow-y-auto" style={{ height: 'calc(100% - 140px)' }}>
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-3 px-2">
                All Categories
              </p>
              <div className="space-y-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-primary-50 active:bg-primary-100 transition-colors duration-150 cursor-pointer group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-primary-100 transition-colors flex-shrink-0">
                      <i className={`${cat.icon} text-base text-foreground-600 group-hover:text-primary-600 transition-colors`}></i>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors block truncate">
                        {cat.name}
                      </span>
                      <span className="text-xs text-foreground-500 truncate block">
                        {cat.description}
                      </span>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-400 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all text-lg flex-shrink-0"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Panel internal links */}
            <div className="px-4 py-3 border-t border-background-200">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wider mb-3 px-2">
                Research Guides
              </p>
              <div className="space-y-1">
                <Link
                  to="/kakobuy-spreadsheet-guide"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-book-open-line text-foreground-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700">Kakobuy Spreadsheet Guide</span>
                </Link>
                <Link
                  to="/kakobuy-spreadsheet-sneakers"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-footprint-line text-foreground-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700">Sneakers Research</span>
                </Link>
                <Link
                  to="/kakobuy-spreadsheet-fashion"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-t-shirt-line text-foreground-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700">Fashion Research</span>
                </Link>
                <Link
                  to="/kakobuy-spreadsheet-best-finds"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-star-line text-foreground-600"></i>
                  </div>
                  <span className="text-sm text-foreground-700">Best Finds</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Panel footer - fixed CTA */}
          <div className="absolute bottom-0 left-0 right-0 px-5 py-4 border-t border-background-200 bg-background-50">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-full px-5 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}